<script setup lang="ts">
import type { ReportTemplateId } from './report-reusables/reportTemplates';

import { useUpdatedReportStore } from '@src/store/report-store';

import { computed, ref } from 'vue';

import { useAlert } from '@/src/composables/useAlert';

import { downloadMultiReportAsPDF } from './report-reusables/renderTemplateToPDF';
import {
  getAllTemplateEntries,

} from './report-reusables/reportTemplates';
import UpdatedReport from './UpdatedReport.vue';

defineProps<{
  width: number;
  height: number;
}>();

const alert = useAlert();
const reportStore = useUpdatedReportStore();
const logoOptions = [
  { text: 'Preventicum', url: '/images/preventicumlogo.png' },
  // { text: 'Telus', url: '/images/telus_logo.png' },
  { text: 'None', url: null },
];

const edit = ref(true);
const showPopup = ref(false);
const key = ref(0);

const selectedLogo = computed(() => {
  const logo = reportStore.getValue('header.addedLogo') as any;
  if (!logo || !logo.url) {
    return '';
  }

  // If the logo has an alt tag matching one of the options (e.g. for base64 loaded options)
  if (logo.alt) {
    const match = logoOptions.find(opt => opt.text === logo.alt);
    if (match) {
      return match.text;
    }
  }

  // Fallback to URL matching (e.g. for loaded json templates/reports)
  const match = logoOptions.find(
    opt =>
      opt.url
      && (logo.url === opt.url
        || logo.url.endsWith(opt.url)
        || opt.url.endsWith(logo.url)),
  );
  if (match) {
    return match.text;
  }
  return 'Custom Logo';
});

/** All available template entries for the Manage Pages dropdown */
const allTemplateEntries = computed(() => getAllTemplateEntries());

/** Currently loaded pages with their labels for the v-for loop */
const reportPages = computed(() => reportStore.pages);

const allreport = ref<HTMLDivElement | null>(null);
const jsonLoader = ref<HTMLInputElement | null>(null);
const editablereport = ref<any>(null);

function togglePage(templateId: ReportTemplateId) {
  if (reportStore.hasPage(templateId)) {
    reportStore.removePage(templateId);
  }
  else {
    reportStore.addPage(templateId);
  }
  key.value++;
}

function updateLogo(logo: { text: string; url: string | null }) {
  if (!logo.url) {
    reportStore.setValue('header.addedLogo', null);
  }
  else {
    const img = new Image();
    img.onload = () => {
      // Create a canvas to convert the static image to a base64 data URL
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        try {
          const base64Url = canvas.toDataURL('image/png');
          reportStore.setValue('header.addedLogo', {
            url: base64Url,
            alt: logo.text,
            width: img.width,
            height: img.height,
          });
          return;
        }
        catch (e) {
          console.error('Failed to convert logo to base64', e);
        }
      }

      // Check if there is a valid url:
      if (!logo.url || logo.url.trim() === '') {
        console.warn('Logo URL is empty or invalid, skipping logo update.');
        return;
      }

      // Fallback to raw URL if canvas fails
      reportStore.setValue('header.addedLogo', {
        url: logo.url,
        alt: logo.text,
        width: img.width,
        height: img.height,
      });
    };
    img.onerror = () => {
      console.warn(
        'Failed to load image to calculate dimensions, using default ratio.',
      );
      if (!logo.url || logo.url.trim() === '') {
        console.warn('Logo URL is empty or invalid, skipping logo update.');
        return;
      }
      reportStore.setValue('header.addedLogo', {
        url: logo.url,
        alt: logo.text,
        width: 100,
        height: 40,
      });
    };

    // Support deployments under a subpath dynamically
    const baseUrl = import.meta.env.BASE_URL || '/';
    const resolvedUrl = (baseUrl + logo.url).replace(/\/+/g, '/');
    img.src = resolvedUrl;
  }
}

function handleEdit() {
  edit.value = !edit.value;

  if (edit.value) {
    // Switch to edit mode
    reportStore.unlockAllFields();
  }
  else {
    // Switch to view mode
    reportStore.lockAllFields();
  }
}

function handleSave() {
  if (edit.value) {
    showPopup.value = true;
  }

  // Build the pages array for multi-page PDF export
  const pagesForPdf = reportStore.getPages().map((tid) => {
    const template = reportStore.getTemplateForPage(tid);
    return {
      template: template!,
      values: reportStore.getAllValues(),
    };
  });

  const currentDate = new Date()
    .toLocaleDateString('en-GB')
    .replace(/\//g, '_');
  const patientIDVal = reportStore.getValue('patientID');
  const patientID
    = typeof patientIDVal === 'string' && patientIDVal.trim()
      ? patientIDVal.trim()
      : 'Patient';
  const safePatientID = patientID.replace(/[^\w-]/g, '_');
  const fileName = `${safePatientID}_${currentDate}_Area19_OS-CMR_report.pdf`;
  downloadMultiReportAsPDF(pagesForPdf, fileName);
}

function handleDownload() {
  try {
    const jsonString = reportStore.serialize();
    const blob = new Blob([jsonString], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const currentDate = new Date()
      .toLocaleDateString('en-GB')
      .replace(/\//g, '_');
    const patientIDVal = reportStore.getValue('patientID');
    const patientID
      = typeof patientIDVal === 'string' && patientIDVal.trim()
        ? patientIDVal.trim()
        : 'Patient';
    const safePatientID = patientID.replace(/[^\w-]/g, '_');
    a.download = `${safePatientID}_${currentDate}_Area19_OS-CMR_report.a19`;
    a.click();
    URL.revokeObjectURL(url);
  }
  catch (error) {
    console.error('Error generating download file:', error);
    alert.error('Failed to download report data.');
  }
}

function openFilePicker() {
  jsonLoader.value?.click();
}

function handleFileUpload(event: Event) {
  const input = event.target as HTMLInputElement;

  const file = input.files?.[0];

  if (!file) {
    alert.warning('Please select a JSON file first.');
    return;
  }

  const reader = new FileReader();

  reader.onload = (e) => {
    try {
      const jsonString = e.target?.result as string;
      reportStore.deserialize(jsonString);
      key.value++;
      // Clear input so selecting the same file again triggers change event
      input.value = '';
    }
    catch (error) {
      console.error('Error parsing JSON:', error);
      alert.error(
        'Error parsing JSON file. Please ensure it is a valid .a19 file.',
      );
    }
  };

  reader.readAsText(file);
}

function reComputeValues() {
  reportStore.resolveAutomaticFieldsInContext();
  key.value++;
}
</script>

<template>
  <div class="reportcontainermain">
    <div class="toolbar-report-container">
      <div ref="allreport" class="reporthere">
        <template v-for="(page, index) in reportPages" :key="page.templateId">
          <!-- Page divider between reports -->
          <div v-if="index > 0" class="page-divider">
            <div class="divider-line" />
            <span class="divider-label">{{ page.label }}</span>
            <div class="divider-line" />
          </div>
          <UpdatedReport ref="editablereport" :template-id="page.templateId" />
        </template>
      </div>

      <div class="toolbar">
        <!-- Moved DashNavigator buttons here -->
        <input
          ref="jsonLoader"
          type="file"
          name="jsonLoader"
          style="display: none"
          accept=".a19"
          aria-labelledby="hiddenjsonLoader"
          @change="handleFileUpload"
        >
        <button
          class="button btn-outline report-button"
          @click="openFilePicker"
        >
          <p>Upload</p>
          <i class="fa-solid fa-circle-chevron-up fa-xs" />
        </button>
        <button
          v-if="!edit"
          class="button btn-outline report-button"
          @click="handleDownload"
        >
          <p>Download</p>
          <i class="fa-solid fa-circle-down fa-xs" />
        </button>

        <button
          class="button btn-outline report-button"
          @click="reComputeValues"
        >
          <p>Re-Sync</p>
          <i class="fa-solid fa-sync fa-xs" />
        </button>

        <!-- Manage Pages dropdown -->
        <v-menu :close-on-content-click="false">
          <template #activator="{ props }">
            <button class="button btn-outline report-button" v-bind="props">
              <p>Pages</p>
              <i class="fa-solid fa-layer-group fa-xs" />
            </button>
          </template>
          <v-list density="compact" class="pages-dropdown">
            <v-list-item
              v-for="entry in allTemplateEntries"
              :key="entry.id"
              @click="togglePage(entry.id)"
            >
              <template #prepend>
                <v-checkbox-btn
                  :model-value="reportStore.hasPage(entry.id)"
                  density="compact"
                  hide-details
                  color="primary"
                  @update:model-value="togglePage(entry.id)"
                />
              </template>
              <v-list-item-title>{{ entry.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-menu v-if="edit">
          <template #activator="{ props }">
            <button class="button btn-primary report-button" v-bind="props">
              <p>{{ selectedLogo || 'Select Logo' }}</p>
              <i class="fa-solid fa-caret-down fa-xs" />
            </button>
          </template>
          <v-list>
            <v-list-item
              v-for="option in logoOptions"
              :key="option.text"
              @click="updateLogo(option)"
            >
              <v-list-item-title>{{ option.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <button
          v-if="!edit"
          class="button btn-outline report-button"
          :class="{ clicked: edit }"
          @click="handleEdit"
        >
          <p>Edit</p>
          <i class="fa-solid fa-pen-to-square fa-xs" />
        </button>

        <button
          v-else
          class="button btn-outline report-button"
          :class="{ clicked: edit }"
          @click="handleEdit"
        >
          <p>Done</p>
          <i class="fa-solid fa-check fa-xs" />
        </button>

        <div
          v-if="!edit"
          style="
            position: relative;
            display: flex;
            flex-direction: column;
            gap: 10px;
          "
        >
          <button
            class="button btn-secondary report-button"
            @click="handleSave"
          >
            <p>Export</p>
          </button>
          <div class="popup" :class="{ show: showPopup }">
            <h5>Please stop editing first.</h5>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.toolbar-report-container {
  display: flex;
  gap: 15px;
}
.separator {
  min-width: 20%;
}
.popup {
  position: absolute;
  left: -105px;
  top: 20px;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.3);
  color: white;
  padding: 10px 15px;
  border-radius: 5px;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease-out;
  width: 180px;
  height: 20px;
}

.popup.show {
  opacity: 1;
}

.reportcontainermain {
  background: rgba(0, 0, 0, 0.5);
  border-radius: 10px;
  display: flex;
  overflow: hidden;
  padding-top: 40px;
  justify-content: center;
  width: 100%;
  height: 90vh;
}

.tool.clicked {
  background: #229ed7e7;
}

.reporthere {
  border-radius: 7px;
  overflow: scroll;
  overflow-x: hidden; /* Hide horizontal scrollbar */
  user-select: all;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.reporthere::-webkit-scrollbar {
  display: none;
  width: 0 !important;
}

.toolbar {
  display: flex;
  flex-direction: column;
  margin-bottom: auto;
  margin-top: 15px;
  gap: 15.5px;
}
.tool {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid #707070;
  color: white;
  background: rgba(255, 255, 255, 0);
  cursor: pointer;
  /* width: 65px; */
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 4px;

  height: 32px;
}

.report-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border-radius: 5px;
  padding: 8px 8px;
}

.tool:hover {
  background: #229ed78c;
  transition: 0.2s;
}
.reset-btn {
  background: rgba(145, 2, 2, 0.2);
}

.reset-btn:hover {
  background: rgba(145, 2, 2, 0.4);
  transition: 0.2s;
}

/* ── Page Divider ────────────────────────────────────────────────────────── */
.page-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 20px;
  user-select: none;
}

.divider-line {
  flex: 1;
  height: 1px;
  background: repeating-linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.35) 0px,
    rgba(255, 255, 255, 0.35) 6px,
    transparent 6px,
    transparent 12px
  );
}

.divider-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

/* ── Pages Dropdown ──────────────────────────────────────────────────────── */
.pages-dropdown {
  min-width: 200px;
}
</style>
